import { api, ServiceName } from './api';
import {
  CreatePurchaseResponse,
  CreateSubscriptionResponse,
  IPlan,
  OrderInfoResponse,
  SubscriptionInfoResponse,
} from '@/models/checkout.model';
import { featureFlag, logger, mixpanelUtils } from '@/utils';
import {
  LOGGER_ACTION,
  LOGGER_FEATURE,
  LOGGER_PROVIDER,
} from '../../constants/logger/logger-tags';

class ApiPremium {
  public async getSubscriptionPlans(source: string): Promise<IPlan[]> {
    try {
      const country = featureFlag.getCurrentCountry();
      const res = await api.httpGet<{ plans: IPlan[] }>(
        ServiceName.PremiumHub,
        `/plans?source=${source}&country=${country}`
      );
      return res.plans;
    } catch (error) {
      logger.error('Failed to load subscription plans', error, {
        tags: {
          feature: LOGGER_FEATURE.PREMIUM,
          action: LOGGER_ACTION.LOAD_PLANS,
          source,
        },
        extra: { country: featureFlag.getCurrentCountry() },
      });
      // Return empty array instead of throwing - let component handle gracefully
      return [];
    }
  }

  public async createPaypalPurchase(
    planId: string,
    currency: string
  ): Promise<string> {
    try {
      return await api.httpPost(ServiceName.Premium, '/createPaypalPurchase', {
        plan_id: planId,
        currency: currency,
      });
    } catch (error) {
      logger.error('Failed to create PayPal purchase', error, {
        tags: {
          feature: LOGGER_FEATURE.PAYMENT,
          provider: LOGGER_PROVIDER.PAYPAL,
          action: LOGGER_ACTION.CREATE_PURCHASE,
        },
        extra: { planId, currency },
      });
      throw error;
    }
  }

  public async addPendingSubscription(
    subscriptionId: string
  ): Promise<boolean> {
    try {
      return await api.httpPost(ServiceName.Premium, '/pendingSubscription', {
        subscription_id: subscriptionId,
      });
    } catch (error) {
      logger.error('Failed to add pending subscription', error, {
        tags: {
          feature: LOGGER_FEATURE.PAYMENT,
          provider: LOGGER_PROVIDER.PAYPAL,
          action: LOGGER_ACTION.PENDING_SUBSCRIPTION,
        },
        extra: { subscriptionId },
      });
      throw error;
    }
  }

  public async approvePurchaseByClient(purchaseId: string): Promise<void> {
    try {
      await api.httpPost(ServiceName.Premium, '/approvePurchaseByClient', {
        purchase_id: purchaseId,
      });
    } catch (error) {
      logger.error('Failed to approve purchase', error, {
        tags: {
          feature: LOGGER_FEATURE.PAYMENT,
          provider: LOGGER_PROVIDER.PAYPAL,
          action: LOGGER_ACTION.APPROVE_PURCHASE,
        },
        extra: { purchaseId },
      });
      throw error;
    }
  }

  public async approveSubscriptionByClient(
    subscriptionId: string
  ): Promise<void> {
    try {
      // TODO:: implement
      const affiliateParam = '';
      const distinctIdParam = '';

      const url = `/approveSubscriptionByClient?subscription_id=${subscriptionId}${affiliateParam}${distinctIdParam}`;
      await api.httpGet(ServiceName.Premium, url);
    } catch (error) {
      logger.error('Failed to approve subscription', error, {
        tags: {
          feature: LOGGER_FEATURE.PAYMENT,
          provider: LOGGER_PROVIDER.PAYPAL,
          action: LOGGER_ACTION.APPROVE_SUBSCRIPTION,
        },
        extra: { subscriptionId },
      });
      throw error;
    }
  }

  public async getPaypalOrderInfo(orderId: string): Promise<OrderInfoResponse> {
    try {
      const url = `/paypalOrderInfo?order_id=${orderId}`;
      return await api.httpGet(ServiceName.Premium, url);
    } catch (error) {
      logger.error('Failed to get PayPal order info', error, {
        tags: {
          feature: LOGGER_FEATURE.PAYMENT,
          provider: LOGGER_PROVIDER.PAYPAL,
          action: LOGGER_ACTION.GET_ORDER_INFO,
        },
        extra: { orderId },
      });
      throw error;
    }
  }

  public async getPaypalSubscriptionInfo(
    subscriptionId: string
  ): Promise<SubscriptionInfoResponse> {
    try {
      const url = `/paypalSubscriptionInfo?subscription_id=${subscriptionId}`;
      return await api.httpGet(ServiceName.Premium, url);
    } catch (error) {
      logger.error('Failed to get PayPal subscription info', error, {
        tags: {
          feature: LOGGER_FEATURE.PAYMENT,
          provider: LOGGER_PROVIDER.PAYPAL,
          action: LOGGER_ACTION.GET_SUBSCRIPTION_INFO,
        },
        extra: { subscriptionId },
      });
      throw error;
    }
  }

  public async createStripeCustomer(
    email: string,
    paymentMethodId: string
  ): Promise<string> {
    try {
      return await api.httpPost(ServiceName.Premium, '/createStripeCustomer', {
        email: email,
        paymentMethodId: paymentMethodId,
      });
    } catch (error) {
      logger.error('Failed to create Stripe customer', error, {
        tags: {
          feature: LOGGER_FEATURE.PAYMENT,
          provider: LOGGER_PROVIDER.STRIPE,
          action: LOGGER_ACTION.CREATE_CUSTOMER,
        },
        extra: { email },
      });
      throw error; // Component handles this in confirmPayment
    }
  }

  public async createStripePurchase(
    customerId: string,
    planId: string,
    paymentMethodId: string
  ): Promise<CreatePurchaseResponse> {
    try {
      return await api.httpPost(ServiceName.Premium, '/stripe/createPurchase', {
        paymentMethodId: paymentMethodId,
        customerId: customerId,
        priceId: planId,
      });
    } catch (error) {
      logger.error('Failed to create Stripe purchase', error, {
        tags: {
          feature: LOGGER_FEATURE.PAYMENT,
          provider: LOGGER_PROVIDER.STRIPE,
          action: LOGGER_ACTION.CREATE_PURCHASE,
        },
        extra: { customerId, planId },
      });
      throw error; // Component handles this in confirmPayment
    }
  }

  public async createStripeSubscription(
    customerId: string,
    planId: string,
    paymentMethodId: string,
    disableTrial?: boolean,
    couponID?: string
  ): Promise<CreateSubscriptionResponse> {
    try {
      const payload: Record<string, any> = {
        customerId: customerId,
        priceId: planId,
        paymentMethodId: paymentMethodId,
        couponID: couponID,
        distinctId: mixpanelUtils.get_distinct_id(),
      };
      if (disableTrial) {
        payload.disableTrial = 'true';
      }
      return await api.httpPost(
        ServiceName.Premium,
        '/stripe/createSubscription',
        payload
      );
    } catch (error) {
      logger.error('Failed to create Stripe subscription', error, {
        tags: {
          feature: LOGGER_FEATURE.PAYMENT,
          provider: LOGGER_PROVIDER.STRIPE,
          action: LOGGER_ACTION.CREATE_SUBSCRIPTION,
        },
        extra: { customerId, planId, hasCoupon: !!couponID, disableTrial },
      });
      throw error; // Component handles this in confirmPayment
    }
  }

  public async update3DSubscriptionStatus(): Promise<void> {
    try {
      const payload: Record<string, any> = {
        distinctId: mixpanelUtils.get_distinct_id(),
      };
      // TODO:: add aff id from cookie if exists, key in constants AFFILIATE_COOKIE_KEY
      return await api.httpPost(
        ServiceName.Premium,
        '/stripe/update3DSubscriptionStatus',
        payload
      );
    } catch (error) {
      logger.error('Failed to update 3D secure subscription status', error, {
        tags: {
          feature: LOGGER_FEATURE.PAYMENT,
          provider: LOGGER_PROVIDER.STRIPE,
          action: LOGGER_ACTION.THREE_D_SUBSCRIPTION_UPDATE,
        },
      });
      throw error;
    }
  }

  public async update3DPurchaseStatus(): Promise<void> {
    try {
      const payload: Record<string, any> = {};
      // TODO:: add aff id from cookie if exists, key in constants AFFILIATE_COOKIE_KEY
      return await api.httpPost(
        ServiceName.Premium,
        '/stripe/update3DPurchaseStatus',
        payload
      );
    } catch (error) {
      logger.error('Failed to update 3D secure purchase status', error, {
        tags: {
          feature: LOGGER_FEATURE.PAYMENT,
          provider: LOGGER_PROVIDER.STRIPE,
          action: LOGGER_ACTION.THREE_D_PURCHASE_UPDATE,
        },
      });
      throw error;
    }
  }
}

export const apiPremium = new ApiPremium();
